// src/components/ui/Layout/Flex.tsx
import React from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './Flex.module.css';

export interface FlexProps {
  children: React.ReactNode;
  direction?: 'row' | 'column';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  wrap?: boolean;
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  // Новые свойства для адаптивности
  mobileDirection?: 'row' | 'column';
  tabletDirection?: 'row' | 'column';
  responsive?: boolean;
  mobileGap?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  mobileAlign?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  mobileJustify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
}

export const Flex: React.FC<FlexProps> = ({
  children,
  direction = 'row',
  justify = 'start',
  align = 'start',
  wrap = false,
  gap = 'none',
  className = '',
  // Новые параметры
  mobileDirection,
  tabletDirection,
  responsive = true,
  mobileGap,
  mobileAlign,
  mobileJustify
}) => {
  // Используем хук для определения типа устройства
  const { isMobile, isTablet } = useResponsive();
  
  // Собираем все классы для компонента
  const getClasses = () => {
    const classes = [styles.flex];
    
    // Основной класс для направления
    classes.push(styles[direction]);
    
    // Классы для выравнивания
    classes.push(styles[`justify${capitalize(justify)}`]);
    classes.push(styles[`align${capitalize(align)}`]);
    
    // Класс для переноса
    if (wrap) {
      classes.push(styles.wrap);
    } else {
      classes.push(styles.nowrap);
    }
    
    // Класс для отступов
    classes.push(styles[`gap${capitalize(gap)}`]);
    
    // Добавляем адаптивные классы, если responsive = true
    if (responsive) {
      // Адаптивное направление для мобильных
      if (isMobile && mobileDirection) {
        classes.push(styles[`mobile${capitalize(mobileDirection)}`]);
      } else if (isMobile && direction === 'row') {
        // По умолчанию меняем строки на колонки на мобильных, если не задано явно
        classes.push(styles.mobileColumn);
      }
      
      // Адаптивное направление для планшетов
      if (isTablet && tabletDirection) {
        classes.push(styles[`tablet${capitalize(tabletDirection)}`]);
      }
      
      // Адаптивные отступы для мобильных
      if (isMobile && mobileGap) {
        classes.push(styles[`mobileGap${capitalize(mobileGap)}`]);
      }
      
      // Адаптивное выравнивание для мобильных
      if (isMobile && mobileAlign) {
        classes.push(styles[`mobileAlign${capitalize(mobileAlign)}`]);
      }
      
      // Адаптивное выравнивание по основной оси для мобильных
      if (isMobile && mobileJustify) {
        classes.push(styles[`mobileJustify${capitalize(mobileJustify)}`]);
      }
    }
    
    // Добавляем пользовательский класс
    if (className) {
      classes.push(className);
    }
    
    return classes.filter(Boolean).join(' ');
  };
  
  // Вспомогательная функция для форматирования строк
  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  
  return (
    <div className={getClasses()}>
      {children}
    </div>
  );
};

export default Flex;